import React from "react";
import { ArrowUpRight } from "lucide-react";
// TwoPracticesFlip intentionally not imported — the clinical/esoteric flip
// cards are hidden from the homepage per iter_17 user request. The component
// file remains in the codebase for a future reactivation.

const PORTRAIT = "https://customer-assets.emergentagent.com/job_practice-portal-15/artifacts/mub68y5n_Facetune_02-11-2025-15-35-15.jpeg";
const INTRO_VIDEO = "/media/austine-intro-720p.mp4";
const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";

/**
 * Austine intro video player (iter_22).
 * • Shows the portrait photo as poster — section looks identical until play.
 * • Does not autoplay; renders a gold-ivory play button overlay matched to
 *   the site's palette. Native controls appear only after the user presses play.
 * • Plays with sound (video is unmuted on click).
 * • Lazy: the <source> element isn't mounted (and therefore no bytes are
 *   fetched) until the container nears the viewport via IntersectionObserver.
 *   Combined with preload="none" this keeps mobile fast.
 */
function AustineIntroVideo() {
    const videoRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [inView, setInView] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setInView(true);
            return undefined;
        }
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setInView(true);
                        io.disconnect();
                        break;
                    }
                }
            },
            { rootMargin: "200px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const handlePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = false;
        setPlaying(true);
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full h-[380px] lg:h-[480px] overflow-hidden bg-ivory"
            data-testid="about-intro-video-wrap"
        >
            <video
                ref={videoRef}
                poster={PORTRAIT}
                preload="none"
                playsInline
                controls={playing}
                className="w-full h-full object-cover object-center block"
                data-testid="about-intro-video"
            >
                {inView ? <source src={INTRO_VIDEO} type="video/mp4" /> : null}
            </video>
            {!playing && (
                <button
                    type="button"
                    onClick={handlePlay}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onFocus={() => setHovered(true)}
                    onBlur={() => setHovered(false)}
                    aria-label="Play a short welcome from Austine"
                    data-testid="about-intro-video-play"
                    className="absolute inset-0 flex items-end justify-start focus:outline-none cursor-pointer"
                >
                    {/* Pill sits in the bottom-left corner with comfortable
                        inset padding so it never touches the photo edges.
                        The whole button fills the frame (inset-0) so the
                        entire photo remains clickable — only the pill is
                        visually styled. */}
                    <span
                        aria-hidden
                        className="m-4 lg:m-5 inline-flex items-center gap-2.5 transition-colors duration-300"
                        style={{
                            background: hovered ? "rgba(44,40,37,0.82)" : "rgba(44,40,37,0.62)",
                            color: "#FAFAF8",
                            padding: "10px 18px 10px 14px",
                            borderRadius: "9999px",
                            backdropFilter: "blur(6px)",
                            WebkitBackdropFilter: "blur(6px)",
                            border: "1px solid rgba(250,250,248,0.22)",
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 12,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            fontWeight: 500,
                            lineHeight: 1,
                        }}
                    >
                        <svg
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden
                        >
                            <path d="M1 1L11 7L1 13V1Z" fill="#FAFAF8" />
                        </svg>
                        Play Video
                    </span>
                </button>
            )}
        </div>
    );
}

export default function About() {
    return (
        <section id="about" data-testid="about-section" className="py-28 lg:py-40" style={{ background: "#F2EDE6" }}>
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    {/* Copy left */}
                    <div className="lg:col-span-8 order-2 lg:order-1" data-reveal>
                        <span className="editorial-eyebrow">Meet Austine</span>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal text-balance">
                            Virtual EMDR therapy in <em className="italic text-gold">Wisconsin & Colorado.</em>
                        </h2>

                        <div className="mt-10 hairline" />

                        <p className="mt-10 text-lg leading-[1.85] text-charcoal-soft">
                            I'm Austine — a licensed professional counselor offering virtual therapy
                            to thoughtful adults in Wisconsin and Colorado. I help people who are
                            struggling with anxiety, trauma, self-doubt, and the lasting weight of
                            painful experiences.
                        </p>

                        <p className="mt-6 text-lg leading-[1.85] text-charcoal-soft">
                            I believe therapy should feel compassionate, safe, and quietly
                            sophisticated — a place where you do not have to explain everything
                            perfectly or have all the right words. We start where you are.
                        </p>

                        <p className="mt-6 text-lg leading-[1.85] text-charcoal-soft">
                            In our work together, you will not have to keep carrying this alone.
                            Therapy can become a place where you understand yourself with more
                            kindness, where you feel safer in your body, and where you reconnect
                            with the parts of yourself that have been buried under years of fear
                            and survival.
                        </p>

                        <blockquote
                            className="mt-10 pull-quote"
                            style={{
                                borderLeft: "3px solid #8A9E85",
                                paddingLeft: "20px",
                                color: "#2C2825",
                            }}
                        >
                            You are not defined by what happened to you.
                        </blockquote>

                        <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5 text-[11px] tracking-[0.32em] uppercase text-charcoal-soft" data-testid="about-credentials">
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-gold text-base leading-none">✦</span>
                                LPC — Wisconsin
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-gold text-base leading-none">✦</span>
                                LPC — Colorado
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-gold text-base leading-none">✦</span>
                                Telehealth Only
                            </div>
                        </div>

                        {/* Trust strip — quiet credentials */}
                        <div
                            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 font-serif italic text-lg sm:text-xl text-gold"
                            data-testid="about-trust-strip"
                        >
                            <span>EMDRIA Training & Certification in Process</span>
                            <span className="text-gold not-italic font-sans text-sm leading-none">✦</span>
                            <span>Brainspotting Practitioner</span>
                        </div>

                        <a
                            href={BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="about-book-button"
                            className="mt-12 editorial-button-primary group"
                        >
                            Schedule a Consultation
                            <ArrowUpRight size={16} strokeWidth={1.25} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>

                    {/* Portrait right — now hosts Austine's short intro video.
                        Poster = original portrait so the frame is visually
                        identical before play. Caption below in italic serif. */}
                    <div className="lg:col-span-4 order-1 lg:order-2" data-reveal>
                        <div className="relative max-w-[360px] lg:max-w-none mx-auto lg:mx-0 lg:ml-auto lg:w-[85%]">
                            <AustineIntroVideo />
                            <div className="absolute -top-3 -left-3 px-4 py-2 bg-ivory border border-gold/40">
                                <span className="font-serif italic text-charcoal text-sm">Austine, LPC</span>
                            </div>
                            <p
                                className="mt-5 font-serif italic text-base text-charcoal-soft text-center lg:text-right"
                                data-testid="about-intro-video-caption"
                            >
                                A short welcome from Austine.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Two flip cards (clinical + other services) removed from the
                    homepage in iter_17 to hide the esoteric side of the
                    practice from public view. TwoPracticesFlip.jsx remains
                    in the codebase and can be reintroduced by re-importing
                    and rendering <TwoPracticesFlip /> here. */}
            </div>
        </section>
    );
}
